const PULL_THRESHOLD_PX = 80;
const PULL_MAX_PX = 120;
const INDICATOR_CLASS = 'pull-refresh-indicator';

function setIndicator(el: HTMLElement, state: 'hidden' | 'pull' | 'busy', dy = 0): void {
  let node = el.querySelector(`.${INDICATOR_CLASS}`) as HTMLElement | null;
  if (state === 'hidden') {
    node?.remove();
    return;
  }
  if (!node) {
    node = document.createElement('div');
    node.className = INDICATOR_CLASS;
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.innerHTML = '<span class="pull-refresh-indicator__icon" aria-hidden="true"></span>';
    el.prepend(node);
  }
  node.classList.toggle('pull-refresh-indicator--busy', state === 'busy');
  node.classList.toggle('pull-refresh-indicator--pulling', state === 'pull');
  const offset = Math.min(Math.max(dy, 0), PULL_MAX_PX);
  node.style.setProperty('--pull-offset', `${offset}px`);
  node.style.setProperty('--pull-progress', String(Math.min(offset / PULL_THRESHOLD_PX, 1)));
}

export function attachPullToRefresh(
  getScrollEl: () => HTMLElement | null,
  onRefresh: () => void | Promise<void>,
): () => void {
  let startY = 0;
  let tracking = false;
  let busy = false;

  const hideIndicator = (): void => {
    const el = getScrollEl();
    if (el) setIndicator(el, 'hidden');
  };

  const onStart = (e: TouchEvent): void => {
    const el = getScrollEl();
    if (!el || el.scrollTop > 2 || e.touches.length !== 1) return;
    tracking = true;
    startY = e.touches[0].clientY;
  };

  const onMove = (e: TouchEvent): void => {
    if (!tracking) return;
    const el = getScrollEl();
    if (!el || el.scrollTop > 2) {
      tracking = false;
      hideIndicator();
      return;
    }
    const dy = e.touches[0].clientY - startY;
    if (dy > 12) e.preventDefault();
    if (dy < 0) {
      tracking = false;
      hideIndicator();
      return;
    }
    if (dy > 0) setIndicator(el, 'pull', dy);
  };

  const onEnd = (e: TouchEvent): void => {
    if (!tracking) return;
    tracking = false;
    const el = getScrollEl();
    const endY = e.changedTouches[0]?.clientY;
    if (!el || endY == null || el.scrollTop > 2 || busy) {
      hideIndicator();
      return;
    }
    const dy = endY - startY;
    if (dy < PULL_THRESHOLD_PX) {
      hideIndicator();
      return;
    }
    busy = true;
    setIndicator(el, 'busy');
    void Promise.resolve(onRefresh()).finally(() => {
      busy = false;
      hideIndicator();
    });
  };

  const el = getScrollEl();
  if (!el) return () => undefined;

  el.addEventListener('touchstart', onStart, { passive: true });
  el.addEventListener('touchmove', onMove, { passive: false });
  el.addEventListener('touchend', onEnd, { passive: true });

  return () => {
    el.removeEventListener('touchstart', onStart);
    el.removeEventListener('touchmove', onMove);
    el.removeEventListener('touchend', onEnd);
    setIndicator(el, 'hidden');
  };
}
